import React, { useState, useEffect } from 'react'
import { Download, Filter, Search, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react'
import api from '../services/api'

const Reports = () => {
  const [reports, setReports] = useState([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const perPage = 10

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get('/reports/list')
        if (res.data?.success) setReports(res.data.reports || [])
      } catch (e) { console.error(e) }
      finally { setLoading(false) }
    }
    fetch()
  }, [])

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1E4D40]" /></div>

  const totalPages = Math.ceil(reports.length / perPage) || 1
  const paginated = reports.slice((page - 1) * perPage, page * perPage)

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Reports</h1>
          <p className="text-gray-500 mt-1">Counterfeit detection reports and analysis</p>
        </div>
        <div className="flex items-center space-x-3 mt-4 sm:mt-0">
          <button className="inline-flex items-center px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 shadow-sm">
            <Download className="h-4 w-4 mr-2" /> Export
          </button>
          <button className="inline-flex items-center px-4 py-2.5 text-sm font-medium text-white bg-[#1E4D40] rounded-xl hover:bg-[#163a30] shadow-sm">
            <Filter className="h-4 w-4 mr-2" /> Filter
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          { label: 'Total Reports', val: reports.length, color: 'text-gray-900' },
          { label: 'Pending Review', val: reports.filter(r => r.status === 'pending' || r.status === 'investigating').length, color: 'text-amber-600' },
          { label: 'Resolved', val: reports.filter(r => r.status === 'resolved' || r.status === 'confirmed').length, color: 'text-green-600' },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-3xl p-6 shadow-sm">
            <p className="text-sm text-gray-500">{s.label}</p>
            <p className={`text-3xl font-bold mt-1 ${s.color}`}>{s.val}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-3xl shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-lg font-semibold text-gray-900">All Reports</h3>
          <div className="relative mt-3 sm:mt-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input className="pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E4D40]/20 focus:border-[#1E4D40]" placeholder="Search reports..." />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50/50">
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Severity</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
              </tr>
            </thead>
            <tbody>
              {paginated.map((r, i) => (
                <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="px-6 py-4 text-sm font-mono text-gray-500">#{r.id?.toString().slice(-6) || i + 1}</td>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{r.description || r.product_name || 'Counterfeit Report'}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{r.location_city || r.city || 'Unknown'}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                      r.severity === 'high' ? 'bg-red-100 text-red-700' : r.severity === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                    }`}>{r.severity || 'medium'}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                      r.status === 'resolved' || r.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                      r.status === 'investigating' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-700'
                    }`}>{r.status || 'pending'}</span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">{r.created_at ? new Date(r.created_at).toLocaleDateString() : 'N/A'}</td>
                </tr>
              ))}
              {paginated.length === 0 && (
                <tr><td colSpan={6} className="px-6 py-12 text-center text-gray-400">No reports found</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="p-4 border-t border-gray-100 flex items-center justify-between">
          <p className="text-sm text-gray-500">Showing {Math.min((page-1)*perPage+1, reports.length)}-{Math.min(page*perPage, reports.length)} of {reports.length}</p>
          <div className="flex items-center space-x-2">
            <button onClick={() => setPage(Math.max(1, page-1))} disabled={page===1} className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
            <span className="text-sm font-medium text-gray-700">{page} / {totalPages}</span>
            <button onClick={() => setPage(Math.min(totalPages, page+1))} disabled={page===totalPages} className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Reports
